import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SpaOutlined, DiamondOutlined, OpacityOutlined, CheckCircleOutlined, WarningAmberOutlined, ArrowForward, VerifiedUserOutlined, CleanHandsOutlined, Inventory2Outlined } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const JewelleryCareGuidePage = () => {
    useEffect(() => {
        document.title = "Jewellery Care Guide | How to Care for Gold Jewellery – MAB Jewels";

        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Learn how to care for gold and silver jewellery. Simple jewellery care tips by MAB Jewels to maintain shine and purity.");
        }
    }, []);

    return (
        <div className="page-container seo-page">
            <div className="seo-content-wrapper">
                <h1>Jewellery Care Guide – Keep Your Jewellery Shining for Years</h1>

                <p className="seo-intro">
                    Jewellery is precious not only because of its value but also because of the emotions attached to it. With proper care, gold and silver jewellery can retain its beauty and shine for generations.
                </p>
                <p>
                    At <strong>MAB Jewels</strong>, we guide our customers on the best jewellery care practices to protect purity, finish, and craftsmanship.
                </p>

                <h2>General Jewellery Care Tips</h2>
                <div style={{ background: '#e3f2fd', padding: '25px', borderRadius: '12px', border: '1px solid #bbdefb', marginBottom: '30px' }}>
                    <p style={{ marginTop: 0, fontWeight: 'bold' }}>Follow these simple rules to maintain your jewellery:</p>
                    <ul className="seo-check-list" style={{ marginTop: '10px' }}>
                        <li><CheckCircleOutlined sx={{ color: '#1976d2', mr: 1, verticalAlign: 'middle' }} /> Wear jewellery after applying makeup, perfume, or deodorant</li>
                        <li><CheckCircleOutlined sx={{ color: '#1976d2', mr: 1, verticalAlign: 'middle' }} /> Remove jewellery before bathing or swimming</li>
                        <li><CheckCircleOutlined sx={{ color: '#1976d2', mr: 1, verticalAlign: 'middle' }} /> Avoid contact with chemicals and detergents</li>
                        <li><CheckCircleOutlined sx={{ color: '#1976d2', mr: 1, verticalAlign: 'middle' }} /> Store jewellery separately to prevent scratches</li>
                    </ul>
                </div>

                <h2>Gold Jewellery Care Tips</h2>
                <p>Gold is durable but still needs proper handling.</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '30px' }}>
                    <div style={{ background: '#f9fff9', padding: '20px', borderRadius: '8px', border: '1px solid #d4edda' }}>
                        <h3 style={{ marginTop: 0, color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
                            <CheckCircleOutlined sx={{ mr: 1 }} /> Do:
                        </h3>
                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            <li style={{ marginBottom: '8px' }}>Clean gold jewellery using mild soap and lukewarm water</li>
                            <li style={{ marginBottom: '8px' }}>Use a soft cloth or soft brush for cleaning</li>
                            <li style={{ marginBottom: '8px' }}>Store gold jewellery in fabric-lined boxes</li>
                        </ul>
                    </div>

                    <div style={{ background: '#fff9f9', padding: '20px', borderRadius: '8px', border: '1px solid #f8d7da' }}>
                        <h3 style={{ marginTop: 0, color: '#c62828', display: 'flex', alignItems: 'center' }}>
                            <WarningAmberOutlined sx={{ mr: 1 }} /> Don't:
                        </h3>
                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            <li style={{ marginBottom: '8px' }}>Don't expose gold to chlorine or harsh chemicals</li>
                            <li style={{ marginBottom: '8px' }}>Don't rub with rough materials</li>
                            <li style={{ marginBottom: '8px' }}>Don't store gold pieces together loosely</li>
                        </ul>
                    </div>
                </div>

                <h2>Silver Jewellery Care Tips</h2>
                <p>Silver jewellery may tarnish over time due to air exposure.</p>

                <div className="seo-steps-list">
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                        <CleanHandsOutlined sx={{ color: '#d4af37', mr: 2, fontSize: 28, mt: 0.5 }} />
                        <div>
                            <strong>Maintenance:</strong> Store silver in airtight containers, clean occasionally with a silver polishing cloth, and keep dry.
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <WarningAmberOutlined sx={{ color: '#d32f2f', mr: 2, fontSize: 28, mt: 0.5 }} />
                        <div>
                            <strong>Avoid:</strong> Don't expose silver to moisture for long periods and don't use toothpaste or harsh cleaners.
                        </div>
                    </div>
                </div>

                <h2>Jewellery Care for Daily Wear vs Occasion Wear</h2>

                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '30px' }}>
                    <div style={{ flex: 1, minWidth: '300px', background: '#fcfcfc', padding: '25px', borderRadius: '12px', border: '1px solid #eee' }}>
                        <h3 style={{ marginTop: 0, color: '#1a1a1a', display: 'flex', alignItems: 'center' }}>
                            <DiamondOutlined sx={{ color: '#d4af37', mr: 1 }} /> Daily Wear
                        </h3>
                        <ul style={{ marginLeft: '20px', marginBottom: 0 }}>
                            <li style={{ marginBottom: '8px' }}>Clean once every 1–2 weeks</li>
                            <li style={{ marginBottom: '8px' }}>Inspect clasps and settings regularly</li>
                        </ul>
                    </div>

                    <div style={{ flex: 1, minWidth: '300px', background: '#fcfcfc', padding: '25px', borderRadius: '12px', border: '1px solid #eee' }}>
                        <h3 style={{ marginTop: 0, color: '#1a1a1a', display: 'flex', alignItems: 'center' }}>
                            <SpaOutlined sx={{ color: '#d4af37', mr: 1 }} /> Occasion / Bridal
                        </h3>
                        <ul style={{ marginLeft: '20px', marginBottom: 0 }}>
                            <li style={{ marginBottom: '8px' }}>Clean before storage</li>
                            <li style={{ marginBottom: '8px' }}>Wrap individually with soft cloth</li>
                            <li style={{ marginBottom: '8px' }}>Avoid frequent handling</li>
                        </ul>
                    </div>
                </div>

                <h2>Jewellery Storage Tips</h2>
                <div className="seo-highlight-box" style={{ background: '#fff8e1', borderLeft: '4px solid #d4af37' }}>
                    <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}>
                        <Inventory2Outlined sx={{ mr: 1 }} /> Proper Storage
                    </h3>
                    <ul className="seo-check-list" style={{ marginTop: '10px' }}>
                        <li>Use separate pouches for each piece</li>
                        <li>Keep jewellery away from sunlight</li>
                        <li>Use silica gel packs to control moisture</li>
                        <li>Lock expensive jewellery securely</li>
                    </ul>
                    <p style={{ margin: '15px 0 0 0', fontSize: '0.9rem', fontStyle: 'italic' }}>Proper storage prevents scratches and discoloration.</p>
                </div>

                <h2>Professional Cleaning & Maintenance</h2>
                <p>We recommend professional cleaning:</p>
                <div className="seo-simple-list">
                    <ul>
                        <li>Once a year for frequently worn jewellery</li>
                        <li>Before special occasions</li>
                        <li>After long-term storage</li>
                    </ul>
                </div>
                <p>At <strong>MAB Jewels</strong>, we provide <Link to="/contact">expert guidance</Link> for jewellery maintenance.</p>

                <h2>Why Follow Jewellery Care Guidelines?</h2>
                <ul className="seo-check-list">
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Maintains shine and finish</li>
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Protects gemstone settings</li>
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Prevents unnecessary damage</li>
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Preserves resale value</li>
                </ul>

                <h2>FAQs – Jewellery Care</h2>
                <div className="seo-faq-section">
                    <div className="seo-faq-item">
                        <h4>Q1. Can I wear gold jewellery daily?</h4>
                        <p>Yes, but proper care and cleaning are essential.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q2. Why does silver jewellery turn black?</h4>
                        <p>Silver reacts with air and moisture, causing tarnish.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q3. Does MAB Jewels provide cleaning advice?</h4>
                        <p>Yes. Our team always guides customers on proper care.</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/bis-hallmarking-guide">BIS Hallmarking Guide</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/gold-jewellery-jodhpur">Gold Jewellery Collection</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/trusted-jewellery-store-jodhpur">Trusted Jewellery Store in Jodhpur</Link></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default JewelleryCareGuidePage;
